"use client";

import { useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { initSecurityChecks } from "@/lib/utils/security";
import { useAuth } from "@/hooks/use-auth";
import { normalizeRelativeReturnUrl } from "@/lib/auth/route-policy";
import { resolveDestination } from "@/lib/auth/desktop-handoff";

/**
 * Auth layout — split-screen with B&W branding panel.
 * Redirects logged-in users to returnUrl or /home.
 */
export function AuthLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoggedIn, isLoading } = useAuth();

  useEffect(() => {
    initSecurityChecks();
  }, []);

  useEffect(() => {
    if (!isLoading && isLoggedIn) {
      const params = new URLSearchParams(location.search);
      const returnUrl = normalizeRelativeReturnUrl(params.get("returnUrl")) ?? resolveDestination("/home");
      navigate(returnUrl, { replace: true });
    }
  }, [isLoading, isLoggedIn, location.search, navigate]);

  return (
    <div className="flex min-h-screen">
      {/* Left panel — branding (desktop only) */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-to-br from-neutral-900 via-neutral-950 to-black">
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-neutral-500/20 rounded-full blur-3xl animate-pulse-slow motion-reduce:animate-none" />
        <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-neutral-400/10 rounded-full blur-3xl animate-pulse-slow [animation-delay:2s] motion-reduce:animate-none" />

        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-16">
          <Link to="/" className="mb-10 inline-flex">
            <img
              src="/logo.png"
              alt="Speak AI"
              className="h-8 w-auto object-contain brightness-0 invert"
              fetchPriority="high"
            />
          </Link>

          <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight tracking-tight">
            {t("AUTH.LAYOUT.HEADLINE_LINE1", "Turn conversations")}
            <br />
            <span className="bg-gradient-to-r from-neutral-200 to-neutral-400 bg-clip-text text-transparent">
              {t("AUTH.LAYOUT.HEADLINE_LINE2", "into insights")}
            </span>
          </h1>

          <p className="mt-4 text-base text-muted-foreground max-w-md leading-relaxed">
            {t("AUTH.LAYOUT.BODY", "Transcribe, analyze, and find patterns in every conversation. Works with audio, video, and text.")}
          </p>

          <div className="mt-10 flex items-center gap-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground" />
              {t("AUTH.LAYOUT.FEATURE_AI", "AI Identifies Patterns")}
            </div>
            <div className="flex items-center gap-2">
              <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground" />
              {t("AUTH.LAYOUT.FEATURE_KNOWLEDGE_BASE", "Searchable Knowledge Base")}
            </div>
          </div>
        </div>
      </div>

      {/* Right panel — form area */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 lg:px-8 bg-background">
        <div className="lg:hidden mb-8">
          <Link to="/" className="inline-flex">
            <img
              src="/logo.png"
              alt="Speak AI"
              className="h-7 w-auto object-contain dark:invert"
              fetchPriority="high"
            />
          </Link>
        </div>

        <div className="w-full max-w-md"><Outlet /></div>
      </div>
    </div>
  );
}
